import {
  addDoc, collection, deleteDoc, deleteField, doc, getDoc, getDocs, serverTimestamp, updateDoc, setDoc, where, limit, query,
} from 'firebase/firestore'
import { isEmpty } from 'lodash'
import { getTenantContextInstance as tenantCtx } from '@/plugins/tenant'
import i18n from '@/libs/i18n'
import store from '@/store'
import {
  matchesSharedIntegration,
  findTenantIntegration,
  buildConfigurationUpdatePatch,
  SHARED_TO_LEGACY_TYPE,
} from '@/views/general-settings/integrations/legacyIntegration'

export default {
  namespaced: true,
  state: {
    list: [],
    current: {},
    activeTab: parseInt(localStorage.getItem('novti-integrations-active-tab'), 10) || 0,
    availableIntegrations: [],
    tenantIntegrations: [],
    error: null,
  },
  getters: {
    getAll: state => state.list,
    getCurrent: state => state.current,
    getActiveTab: state => state.activeTab,
    getNativeIntegrations: state => state.list.filter(el => el.type !== 'custom'),
    getCustomIntegrations: state => state.list.filter(el => el.type === 'custom'),
    isAuthorized: state => !isEmpty(state.current.data?.authorization?.response?.refresh_token),
    getAvailableIntegrations: (state) => {
      return [...state.availableIntegrations].sort((a, b) => {
        const aUnavailable = a.available === false
        const bUnavailable = b.available === false

        if (aUnavailable !== bUnavailable) {
          return aUnavailable ? 1 : -1
        }

        return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      })
    },
    getTenantIntegrations: state => state.tenantIntegrations,
    getActiveIntegrations: (state, getters) => {
      return getters.getTenantIntegrations.filter(
        integration => integration.status === 'active' || (!integration.status && integration.type && integration.type !== 'custom')
      )
    },
    isIntegrationActive: (state, getters) => (sharedIntegrationId) => {
      return getters.getTenantIntegrations.some(
        integration => matchesSharedIntegration(integration, sharedIntegrationId)
      )
    },
    getTenantIntegrationBySharedId: (state, getters) => (sharedIntegrationId) => {
      return findTenantIntegration(getters.getTenantIntegrations, sharedIntegrationId)
    },
  },
  mutations: {
    UPDATE_LIST(state, payload) {
      state.list = payload
    },
    SET_CURRENT(state, payload) {
      state.current = {
        snapshot: payload,
        data: { ...payload.data(), id: payload.id },
      }
    },
    UNSET_CURRENT(state) {
      state.current = {}
    },
    UPDATE_ACTIVE_TAB(state, payload) {
      localStorage.setItem('novti-integrations-active-tab', payload)
      state.activeTab = payload
    },
    SET_AVAILABLE_INTEGRATIONS(state, payload) {
      state.availableIntegrations = payload
    },
    SET_TENANT_INTEGRATIONS(state, payload) {
      state.tenantIntegrations = payload
    },
    SET_ERROR(state, payload) {
      state.error = payload
    },
  },
  actions: {
    /**
     * Fetches all.
     *
     * @param {Object} obj
     * @param {Object} obj.commit Commit event
     *
     * @returns {Promise}
     */
    fetchAll({ commit }) {
      return new Promise((resolve, reject) => {
        const { integrations } = tenantCtx()

        getDocs(integrations)
          .then(querySnapshot => {
            const data = querySnapshot.docs
              .map(docRef => ({
                id: docRef.id,
                ...docRef.data(),
                uniqueIdentifier: docRef.data()?.identifier?.replace('custom-integration:', ''),
              }))

            commit('UPDATE_LIST', data)
            resolve(data)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('Integrations') }),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches a document by the given ID
     *
     * @param {string} id The document ID
     *
     * @returns {Promise} Retrieves all fields in the document as an Object. Returns undefined if the document doesn't exist.
     */
    fetchById({}, id) {
      return new Promise((resolve, reject) => {
        const { integrations } = tenantCtx()
        const docRef = doc(integrations, id)

        getDoc(docRef)
          .then(docSnapshot => {
            if (!docSnapshot.exists()) {
              reject(new Error('404'))
            }

            resolve(docSnapshot)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('Integration') }),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches a document by the given type.
     *
     * @param {string} type The integration type
     *
     * @returns {Promise} Retrieves all fields in the document as an Object. Returns undefined if the document doesn't exist.
     */
    fetchByType({}, type) {
      return new Promise((resolve, reject) => {
        const { integrations } = tenantCtx()

        const q = query(
          integrations,
          where('type', '==', type),
          where('deletedAt', '==', null),
          limit(1),
        )

        getDocs(q)
          .then(querySnapshot => {
            const [docSnapshot] = querySnapshot.docs

            resolve(docSnapshot)
          })
          .catch(error => reject(error))
      })
    },

    /**
     * Fetches a document by the current item ID
     *
     * @param {string} id
     */
    fetchCurrent({ dispatch, commit }, id) {
      return new Promise((resolve, reject) => {
        dispatch('fetchById', id)
          .then(res => {
            commit('SET_CURRENT', res)
            resolve(res)
          })
          .catch(error => reject(error))
      })
    },

    /**
     * Updates a document by the given payload.
     *
     * @param {Object} obj
     * @param {Object} payload Given payload
     *
     * @returns {Promise<boolean|Error>}
     */
    update({}, payload) {
      return new Promise((resolve, reject) => {
        const { integrations } = tenantCtx()
        const docRef = doc(integrations, payload.id)
        const updatePayload = { ...payload }

        // Keep environment-specific fields scoped only to environments.
        if (updatePayload.type === 'custom') {
          if (updatePayload.useEnvironments) {
            updatePayload.uri = deleteField()
            updatePayload.method = deleteField()
            updatePayload.payloadType = deleteField()
            updatePayload.headers = deleteField()
            updatePayload.params = deleteField()
            updatePayload.variables = deleteField()
            updatePayload.mappingObjects = deleteField()
            updatePayload.authorization = deleteField()
            updatePayload.authentication = deleteField()
          } else {
            updatePayload.production = deleteField()
            updatePayload.sandbox = deleteField()
          }
        }

        // Always refresh the updatedAt timestamp on save
        updatePayload.updatedAt = serverTimestamp()

        getDoc(docRef).then(() => {
          updateDoc(docRef, updatePayload)
            .then(() => {
              store.dispatch('notify', { body: i18n.t('{title} has been updated', { title: 'Integration' }) })
              resolve(true)
            })
            .catch(error => {
              store.dispatch('notify', {
                body: i18n.t('Something went wrong updating the {title}', { title: i18n.t('Integration') }),
                variant: 'danger',
              })

              reject(error)
            })
        })
      })
    },

    /**
     * Creates a document by the given payload.
     *
     * @param {Object} obj
     * @param {Object} payload Given payload
     *
     * @returns {Promise<DocumentReference|Error>}
     */
    create({}, payload) {
      return new Promise((resolve, reject) => {
        const { integrations } = tenantCtx()

        addDoc(integrations, {
          ...payload,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          deletedAt: null,
        })
          .then(newDocRef => {
            store.dispatch('notify', {
              title: i18n.t('Great!'),
              body: i18n.t('A new {title} has been created 🚀', { title: i18n.t('Integration') }),
            })

            resolve(newDocRef)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong creating the {title}', { title: i18n.t('Integration') }),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Create or update the record depending if the document id
     * can be found within the given payload
     *
     * @param {Object} obj
     * @param {Object} obj.dispatch  Dispatch event
     * @param {Object} payload       Given payload
     */
    upsert({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        const action = payload?.id !== undefined ? 'update' : 'create'

        console.log(payload)

        dispatch(action, payload).then(ref => {
          resolve(ref)
        }).catch(error => {
          reject(error)
        })
      })
    },

    /**
     * Soft deletes a document
     *
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    delete({}, payload) {
      return new Promise((resolve, reject) => {
        const { integrations } = tenantCtx()
        const docRef = doc(integrations, payload.id)

        deleteDoc(docRef)
          .then(() => resolve(true))
          .catch(err => reject(err))
      })
    },

    /**
     * Fetches all available integrations from sharedIntegrations collection
     *
     * @param {Object} obj
     * @param {Object} obj.commit Commit event
     *
     * @returns {Promise}
     */
    async fetchAvailableIntegrations({ commit }) {
      try {
        const ctx = tenantCtx()
        let { sharedIntegrations, admin } = ctx || {}
        // Fallback if tenant singleton was booted before sharedIntegrations existed
        if (!sharedIntegrations && admin) {
          sharedIntegrations = collection(admin, '_shared/integrations')
          if (ctx) ctx.sharedIntegrations = sharedIntegrations
        }
        if (!sharedIntegrations) {
          throw new Error('Shared integrations collection is not available')
        }

        const querySnapshot = await getDocs(sharedIntegrations)
        const data = querySnapshot.docs.map(docRef => ({
          id: docRef.id,
          ...docRef.data(),
        }))

        commit('SET_AVAILABLE_INTEGRATIONS', data)
        return data
      } catch (error) {
        commit('SET_ERROR', error)
        console.error('Error fetching available integrations:', error)
        throw error
      }
    },

    /**
     * Fetches tenant's activated integrations
     *
     * @param {Object} obj
     * @param {Object} obj.commit Commit event
     *
     * @returns {Promise}
     */
    async fetchTenantIntegrations({ commit }) {
      try {
        const { integrations } = tenantCtx()
        if (!integrations) {
          throw new Error('Tenant integrations collection is not available')
        }

        const querySnapshot = await getDocs(integrations)
        const data = querySnapshot.docs.map(docRef => ({
          id: docRef.id,
          ...docRef.data(),
        }))

        commit('SET_TENANT_INTEGRATIONS', data)
        return data
      } catch (error) {
        commit('SET_ERROR', error)
        console.error('Error fetching tenant integrations:', error)
        throw error
      }
    },

    /**
     * Activates an integration
     *
     * @param {Object} obj
     * @param {Object} obj.commit Commit event
     * @param {Object} obj.dispatch Dispatch event
     * @param {Object} integrationData Integration data from shared integration
     *
     * @returns {Promise}
     */
    async activateIntegration({ commit, dispatch }, integrationData) {
      try {
        const { integrations } = tenantCtx()
        if (!integrations) {
          throw new Error('Tenant integrations collection is not available')
        }

        // Re-use an existing legacy/native activation instead of creating a duplicate
        const existing = findTenantIntegration(
          (await getDocs(integrations)).docs.map(docRef => ({ id: docRef.id, ...docRef.data() })),
          integrationData.id,
        )
        if (existing) {
          await dispatch('fetchTenantIntegrations')
          return doc(integrations, existing.id)
        }

        const newIntegrationRef = doc(integrations)
        const legacyType = SHARED_TO_LEGACY_TYPE[integrationData.id]
        const integrationToSave = {
          ...integrationData,
          sharedIntegrationId: integrationData.id,
          type: legacyType || integrationData.type || integrationData.id,
          status: 'active',
          activatedAt: new Date(),
          configuration: {},
        }

        delete integrationToSave.id

        await setDoc(newIntegrationRef, integrationToSave)
        await dispatch('fetchTenantIntegrations')

        return newIntegrationRef
      } catch (error) {
        commit('SET_ERROR', error)
        console.error('Error activating integration:', error)
        throw error
      }
    },

    /**
     * Deactivates an integration
     *
     * @param {Object} obj
     * @param {Object} obj.commit Commit event
     * @param {Object} obj.dispatch Dispatch event
     * @param {string} tenantIntegrationId Tenant integration document ID
     *
     * @returns {Promise}
     */
    async deactivateIntegration({ commit, dispatch }, tenantIntegrationId) {
      try {
        const { integrations } = tenantCtx()
        if (!integrations) {
          throw new Error('Tenant integrations collection is not available')
        }

        const integrationRef = doc(integrations, tenantIntegrationId)
        await deleteDoc(integrationRef)
        await dispatch('fetchTenantIntegrations')

        return true
      } catch (error) {
        commit('SET_ERROR', error)
        console.error('Error deactivating integration:', error)
        throw error
      }
    },

    /**
     * Updates integration configuration
     *
     * @param {Object} obj
     * @param {Object} obj.commit Commit event
     * @param {Object} obj.dispatch Dispatch event
     * @param {Object} payload Payload with tenantIntegrationId and configuration
     *
     * @returns {Promise}
     */
    async updateIntegrationConfiguration({ commit, dispatch }, { tenantIntegrationId, configuration }) {
      try {
        const { integrations } = tenantCtx()
        if (!integrations) {
          throw new Error('Tenant integrations collection is not available')
        }

        const integrationRef = doc(integrations, tenantIntegrationId)
        const integrationDoc = await getDoc(integrationRef)

        if (!integrationDoc.exists()) {
          throw new Error('Integration not found')
        }

        const patch = buildConfigurationUpdatePatch(integrationDoc.data(), configuration)
        await updateDoc(integrationRef, patch)
        await dispatch('fetchTenantIntegrations')

        return true
      } catch (error) {
        commit('SET_ERROR', error)
        console.error('Error updating integration configuration:', error)
        throw error
      }
    },
  },
}
